<template>
  <div ref="pdfContent" class="pdf-content">
    <CCard>
      <CCardHeader class="no-print">
        <div class="float-right no-print">
          <CButton
            color="success"
            size="sm"
            class="ml-1"
            @click="downloadPDF"
            :disabled="isGeneratingPDF"
          >
            <CIcon name="cil-cloud-download" class="mr-1" />
            {{ isGeneratingPDF ? 'Generating...' : 'Download PDF' }}
          </CButton>
          <CButton color="info" size="sm" class="ml-1" @click="printContent">
            <CIcon name="cil-print" class="mr-1" /> Print
          </CButton>
        </div>
      </CCardHeader>
      <CCardBody>
        <CRow class="justify-content-between align-items-center">
          <CCol>
            <img
              :src="getImageUrl()"
              alt="Logo Syarikat"
              class="img-fluid logo-image"
            />
          </CCol>
          <CCol class="text-right">
            <h1>PAYMENT VOUCHER</h1>
            <h5>{{ document.business.name }}</h5>
            <p>{{ "(" + document.business.regNo + ")" }}</p>
            <p v-html="formatAddress(document.business.address)"></p>
            <p>Tel: {{ document.business.phone }}</p>
          </CCol>
        </CRow>
        <hr class="thick-hr" />
        <br />
        <div class="invoice-details">
          <CRow>
            <CCol sm="6">
              <p><strong>Vendor details</strong></p>
              <p>
                <strong>{{ getVendorName() }}</strong>
              </p>
              <p v-html="formatAddress(getVendorAddress())"></p>
              <p v-if="document.vendor && document.vendor.contactPhone">
                Tel: {{ document.vendor.contactPhone }}
              </p>
              <p v-if="document.vendor && document.vendor.contactEmail">
                Email: {{ document.vendor.contactEmail }}
              </p>
            </CCol>
            <CCol sm="6" class="text-right">
              <dl class="row">
                <dt class="col-sm-6">Voucher no.:</dt>
                <dd class="col-sm-6">{{ document.voucherNumber }}</dd>
                <dt class="col-sm-6">Voucher date:</dt>
                <dd class="col-sm-6">{{ getVoucherDate() }}</dd>
                <dt class="col-sm-6">Payment date:</dt>
                <dd class="col-sm-6">{{ getPaymentDate() }}</dd>
                <dt class="col-sm-6">Payment method:</dt>
                <dd class="col-sm-6">{{ document.paymentMethod || 'N/A' }}</dd>
                <dt class="col-sm-6">Reference:</dt>
                <dd class="col-sm-6">{{ document.paymentReference || 'N/A' }}</dd>
              </dl>
            </CCol>
          </CRow>
          <CRow v-if="document.payeeName">
            <CCol sm="6">
              <p><strong>Payee name: </strong> {{ document.payeeName }}</p>
            </CCol>
          </CRow>
          <CRow v-if="document.description">
            <CCol>
              <p><strong>Description: </strong> {{ document.description }}</p>
            </CCol>
          </CRow>
        </div>

        <div class="table-wrapper">
          <table class="table table-striped">
            <thead>
              <tr>
                <th class="index-column">#</th>
                <th class="item-column">Type</th>
                <th class="item-column">Reference</th>
                <th class="item-column">Description</th>
                <th class="text-right price-column">
                  Original amount (RM)
                </th>
                <th class="text-right price-column">
                  Previously paid (RM)
                </th>
                <th class="text-right price-column">
                  Paying now (RM)
                </th>
                <th class="text-right total-column">
                  Balance (RM)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in document.items" :key="item.id || index">
                <td class="index-column">{{ index + 1 }}</td>
                <td class="item-column">
                  {{ getItemTypeLabel(item.itemType) }}
                </td>
                <td class="item-column">
                  {{ item.referenceNumber || 'N/A' }}
                </td>
                <td class="item-column">
                  {{ item.description || 'N/A' }}
                </td>
                <td class="text-right price-column">
                  {{ formatMyr(item.originalAmount) }}
                </td>
                <td class="text-right price-column">
                  {{ formatMyr(item.previouslyPaid) }}
                </td>
                <td class="text-right price-column">
                  {{ formatMyr(item.amount) }}
                </td>
                <td class="text-right total-column">
                  {{ formatMyr(item.remainingBalance) }}
                </td>
              </tr>

              <!-- Grand Total Row -->
              <tr>
                <td colspan="6" class="text-right">
                  <strong>Total payment amount</strong>
                </td>
                <td class="text-right" colspan="2">
                  <strong>RM {{ grandTotal }}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Notes Section -->
        <div v-if="document.notes" class="notes-section mt-4">
          <p><strong>Notes</strong></p>
          <p v-html="formatNote(document.notes)"></p>
        </div>

        <!-- Signature Section -->
        <div class="signature-section mt-5">
          <CRow>
            <CCol sm="4" class="text-center">
              <div class="signature-line">
                <hr />
                <p class="mb-0"><strong>Prepared by</strong></p>
                <p class="small text-muted">{{ document.createdBy || 'N/A' }}</p>
              </div>
            </CCol>
            <CCol sm="4" class="text-center">
              <div class="signature-line">
                <hr />
                <p class="mb-0"><strong>Approved by</strong></p>
                <p class="small text-muted">{{ document.approvedBy || 'N/A' }}</p>
              </div>
            </CCol>
            <CCol sm="4" class="text-center">
              <div class="signature-line">
                <hr />
                <p class="mb-0"><strong>Received by (vendor)</strong></p>
                <p class="small text-muted">Date: ______________</p>
              </div>
            </CCol>
          </CRow>
        </div>

        <div class="report-footer">
          <p>
            {{ document.business.name }} | {{ document.business.website }} |
            {{ document.business.phone }}
          </p>
          <p>
            Powered By
            <img
              src="/logo.png"
              alt="Niaga-ku Logo"
              class="footer-logo"
            />  Niaga-Ku.com
          </p>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import html2pdf from "html2pdf.js";
import { formatMyr } from "@/utils/money";
import { formatDate } from "@/utils/datetime";

export default {
  name: "WidgetsReportPaymentVoucher",
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isGeneratingPDF: false,
    };
  },
  computed: {
    grandTotal() {
      if (!this.document.items || this.document.items.length === 0) {
        return formatMyr(0);
      }
      return formatMyr(
        this.document.items.reduce((acc, item) => {
          return acc + (parseFloat(item.amount) || 0);
        }, 0)
      );
    },
    isMobile() {
      return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
  },
  mounted() {
    this.forceA4Size();
  },
  methods: {
    formatMyr,

    formatNote(note) {
      return note.replace(/\n/g, "<br />");
    },

    getItemTypeLabel(type) {
      const labels = {
        0: "Bill",
        1: "Expense",
        2: "Claim",
        3: "Advance",
        4: "Loan",
        99: "Other",
      };
      return labels[type] || "Unknown";
    },

    getVoucherDate() {
      return formatDate(this.document.voucherDate);
    },

    getPaymentDate() {
      if (!this.document.paymentDate) return "N/A";
      return formatDate(this.document.paymentDate);
    },

    getVendorAddress() {
      try {
        return this.document.vendor.address;
      } catch (error) {
        return "N/A";
      }
    },

    getVendorName() {
      try {
        return this.document.vendor.name;
      } catch (error) {
        return "N/A";
      }
    },

    removeTrailingSlash(str) {
      return str.endsWith("/") ? str.slice(0, -1) : str;
    },

    getImageUrl() {
      try {
        return (
          this.removeTrailingSlash(window.apiUrl) + this.document.business.logoUrl
        );
      } catch (error) {
        return "";
      }
    },

    formatAddress(address) {
      if (!address) return "N/A";
      return address.replace(/\n/g, "<br />");
    },

    forceA4Size() {
      const pdfContent = this.$refs.pdfContent;
      if (pdfContent) {
        pdfContent.style.width = "210mm";
        pdfContent.style.height = "297mm";
      }
    },

    printContent() {
      if (this.isMobile) {
        this.downloadPDF();
        return;
      }
      this.openPrintWindow();
    },

    openPrintWindow() {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      const printContent = this.$refs.pdfContent.innerHTML;
      const printStyles = this.getPrintStyles();

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${this.document.business.shortName || 'PaymentVoucher'}_${this.document.voucherNumber || 'PV'}</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <style>
              ${printStyles}
            </style>
          </head>
          <body>
            <div class="pdf-content">
              ${printContent}
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      };
    },

    async downloadPDF() {
      this.isGeneratingPDF = true;

      try {
        const opt = {
          margin: [0.5, 0.5, 0.5, 0.5],
          filename: `${this.document.business.shortName || 'PV'}_${this.document.voucherNumber || 'PaymentVoucher'}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
        };

        await html2pdf().set(opt).from(this.$refs.pdfContent).save();
      } catch (error) {
        console.error('PDF generation failed:', error);
        this.$toast.error("Couldn't create the PDF. Please try again.");
      } finally {
        this.isGeneratingPDF = false;
      }
    },

    getPrintStyles() {
      return `
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: Arial, sans-serif;
          color: #333;
          background: white;
          padding: 20px;
        }
        .pdf-content {
          width: 210mm;
          min-height: 297mm;
          margin: 0 auto;
          background: white;
          padding: 15mm;
        }
        h1, h5 {
          margin: 5px 0;
        }
        .thick-hr {
          border: 2px solid #333;
          margin: 10px 0;
        }
        .logo-image {
          max-width: 150px;
          max-height: 80px;
        }
        .table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
        }
        .table th,
        .table td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: left;
        }
        .table thead tr {
          background-color: #f8f9fa;
        }
        .table-striped tbody tr:nth-of-type(odd) {
          background-color: rgba(0,0,0,.02);
        }
        .text-right {
          text-align: right;
        }
        .text-center {
          text-align: center;
        }
        .signature-line hr {
          border: 1px solid #333;
          margin-top: 60px;
          margin-bottom: 5px;
        }
        .report-footer {
          margin-top: 40px;
          text-align: center;
          font-size: 12px;
          color: #666;
        }
        .footer-logo {
          height: 20px;
          vertical-align: middle;
        }
        .no-print {
          display: none;
        }
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            padding: 0;
          }
        }
      `;
    },
  },
};
</script>

<style scoped>
.pdf-content {
  width: 210mm;
  min-height: 297mm;
  margin: 0 auto;
  background: white;
  padding: 15mm;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.logo-image {
  max-width: 150px;
  max-height: 80px;
  object-fit: contain;
}

.thick-hr {
  border: 2px solid #333;
  margin: 10px 0;
}

.table-wrapper {
  overflow-x: auto;
  margin: 20px 0;
}

.signature-section {
  margin-top: 60px;
}

.signature-line hr {
  border: 1px solid #333;
  margin-top: 60px;
  margin-bottom: 5px;
}

.report-footer {
  margin-top: 40px;
  text-align: center;
  font-size: 12px;
  color: #666;
  border-top: 1px solid #ddd;
  padding-top: 20px;
}

.footer-logo {
  height: 20px;
  vertical-align: middle;
}

.index-column {
  width: 50px;
}

.item-column {
  width: auto;
}

.quantity-column {
  width: 100px;
}

.price-column {
  width: 120px;
}

.total-column {
  width: 120px;
}

@media print {
  .no-print {
    display: none !important;
  }

  .pdf-content {
    box-shadow: none;
    padding: 10mm;
  }
}

@media screen and (max-width: 768px) {
  .pdf-content {
    width: 100%;
    padding: 10px;
  }
}
</style>
